import {
  DayCellContentArg,
  DayHeaderContentArg,
  EventClickArg,
  MoreLinkArg,
} from '@fullcalendar/react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { usePopper } from 'react-popper';
import { Icon } from '../../../UI';
import { CALENDAR_VIEW, ExtendedProps } from '../types';
import { getEventBackgroundColor, getEventUrl } from '../helpers';

interface ContentInjectionProps<T> {
  info: T;
  view: CALENDAR_VIEW;
  width: number;
}

/* CUSTOM CELL FOR CALENDAR */
export function dayCellContent({
  info,
  view,
}: ContentInjectionProps<DayCellContentArg>) {
  const date = moment(info.date);
  if (view === 'dayGridMonth')
    return (
      <div className="w-full self-start flex items-center gap-1">
        <p className="text-sm text-black dark:text-white font-medium m-1">
          {date.format('DD')}
        </p>
        {info.isToday && (
          <div className="w-3 h-3 bg-primary rounded-full"></div>
        )}
      </div>
    );
  else return null;
}

/* CUSTOM CALENDAR HEADER */
export function dayHeaderContent({
  info,
  view,
  width,
}: ContentInjectionProps<DayHeaderContentArg>) {
  const date = moment(info.date);

  if (width === 1) {
    return (
      <div className="flex items-center">
        <p className="mr-1 text-xs">{date.format('dd').split('')[0]}</p>
        <p className="text-black dark:text-white text-sm">{date.format('D')}</p>
        {info.isToday && (
          <div className="w-2 h-2 bg-primary rounded-full ml-1"></div>
        )}
      </div>
    );
  }

  if (view === 'timeGridWeek') {
    return (
      <div className="flex gap-2 items-center py-1">
        <p className="text-xs text-grey-50">{date.format('ddd')}</p>
        <p className="text-base text-black dark:text-white">
          {date.format('DD')}
        </p>
        {info.isToday && (
          <div className="w-3 h-3 bg-primary rounded-full"></div>
        )}
      </div>
    );
  } else {
    return (
      <div className="py-2">
        <p className="text-xs text-grey-50">{date.format('ddd')}</p>
      </div>
    );
  }
}

/* EVENT MODAL */
export const EventInfo: React.FC<{
  clickedEvent: EventClickArg | null;
  closeHandler: () => void;
}> = ({ clickedEvent, closeHandler }) => {
  const [referenceEl, setReferenceEl] = useState<HTMLElement | null>(null);
  const [arrowEl, setArrowEl] = useState<HTMLElement | null>(null);
  const [modalEl, setModalEl] = useState<HTMLDivElement | null>(null);

  const event = clickedEvent?.event;
  const extendedProps = event?.extendedProps as ExtendedProps;

  // const [calendarScrollableArea, setCalendarScrollableArea] = useState<
  //   undefined | Element
  // >(undefined);
  //
  // useEffect(() => {
  //   // const scrollableContainer = document.querySelector(
  //   //   '.fc-scroller.fc-scroller-liquid-absolute'
  //   // )!;
  //   const scrollableContainer = document.querySelector(
  //     '.fc-scroller.fc-scroller-liquid-absolute'
  //   );
  //   console.log('calendar::scrollableContainer', scrollableContainer);
  //   setCalendarScrollableArea(scrollableContainer!);
  // }, []);

  const popper = usePopper(referenceEl, modalEl, {
    placement: 'right',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, -4],
        },
      },
      {
        name: 'arrow',
        options: { element: arrowEl, padding: 15 },
      },
      {
        name: 'preventOverflow',
        options: {
          boundary: 'clippingParents',
          altAxis: false,
          tether: false,
        },
      },
      {
        name: 'hide',
      },
    ],
  });

  useEffect(() => {
    if (clickedEvent) {
      setReferenceEl(clickedEvent.el);
      popper.update?.();
    }
  }, [clickedEvent, popper.update]);

  useEffect(() => {
    function closeOnEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        closeHandler();
      }
    }

    window.addEventListener('keydown', closeOnEscape);

    return () => {
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [closeHandler]);

  return (
    <div
      className={`${clickedEvent ? 'block' : 'hidden'} z-5`}
      id="calendar-popper"
      ref={setModalEl}
      style={popper.styles.popper}
      {...popper.attributes.popper}
    >
      <div ref={setArrowEl} style={popper.styles.arrow} id="arrow"></div>
      <div
        className="
        bg-white rounded-2xl border border-grey-10 p-5 max-w-80 overflow-hidden flex gap-3"
      >
        <div>
          {/*<img src={image} alt="Event" className="w-5 object-cover" />*/}
        </div>
        <div className="flex-1 flex flex-col gap-px">
          <p className="text-grey-70 uppercase text-sm">
            {extendedProps?.organizationName}
          </p>
          <p className="text-md text-black font-semibold">
            {extendedProps?.description}
          </p>
          <p className="text-blue text-sm">
            {event?.start ? moment(event.start).format('HH.mm A') : null}
          </p>
          <p className="text-grey-50 text-sm">
            {extendedProps?.longDescription}
          </p>
          <p
            className={
              'text-grey-50 text-sm mt-2 cursor-pointer hover:text-black'
            }
            onClick={() => window.open(getEventUrl(extendedProps?.eventUrl))}
          >
            {'>'}
            {'>'} View events website
          </p>
        </div>
        <div>
          <Icon
            svgName="close"
            className="w-3 h-3 cursor-pointer"
            onClick={closeHandler}
          />
        </div>
      </div>
    </div>
  );
};

/* "MORE" EVENTS MODAL */
export const MoreEvents: React.FC<{
  clickedEvent: MoreLinkArg | null;
  closeHandler: () => void;
}> = ({ clickedEvent, closeHandler }) => {
  const [referenceEl, setReferenceEl] = useState<any | null>(null);
  const [modalEl, setModalEl] = useState<HTMLDivElement | null>(null);

  const popper = usePopper(referenceEl, modalEl, {
    placement: 'top-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, -50],
        },
      },

      {
        name: 'preventOverflow',
        options: {
          boundary: 'clippingParents',
          altAxis: false,
          tether: false,
        },
      },
      {
        name: 'hide',
      },
    ],
  });

  useEffect(() => {
    if (clickedEvent) {
      setReferenceEl(clickedEvent.jsEvent.target);
      popper.update?.();
    }
  }, [clickedEvent, popper.update]);

  useEffect(() => {
    function closeOnEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        closeHandler();
      }
    }

    window.addEventListener('keydown', closeOnEscape);

    return () => {
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [closeHandler]);

  const { date, allSegs } = clickedEvent || {};
  return (
    <div
      className={`${clickedEvent ? 'block' : 'hidden'} z-5`}
      id="calendar-popper"
      ref={setModalEl}
      style={popper.styles.popper}
      {...popper.attributes.popper}
    >
      <div
        className="
        bg-white rounded-2xl border border-grey-30 p-4 w-48 max-w-80 max-h-60 overflow-y-auto scrollbar-hide relative"
        style={{ boxShadow: '0 5px 10px rgba(0,0,0,.1)' }}
      >
        <div className="flex flex-col items-center justify-between w-full">
          <p className="text-xs text-grey-50">{moment(date).format('ddd')}</p>
          <p className="text-base font-medium">{moment(date).format('D')}</p>
        </div>
        <div>
          {allSegs?.map(({ event }, i) => {
            return (
              <div
                key={i}
                className="rounded py-1 px-2 mt-2 text-sm cursor-pointer hover:shadow-light"
                onClick={() =>
                  window.open(getEventUrl(event.extendedProps.eventUrl))
                }
                style={{
                  background: getEventBackgroundColor(
                    event?.extendedProps.color
                  ),
                }}
              >
                {event.title}
              </div>
            );
          })}
        </div>

        <Icon
          svgName="close"
          className="w-3 h-3 cursor-pointer absolute right-4 top-4"
          onClick={closeHandler}
        />
      </div>
    </div>
  );
};
